import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import IconArrow from '../../components/icons/IconArrow';
import IconDelete from '../../components/icons/IconDelete';
import IconEdit from '../../components/icons/IconEdit';
import IconGrip from '../../components/icons/IconGrip';
import IconVisible from '../../components/icons/IconVisible';
import IconVisibleOff from '../../components/icons/IconVisibleOff';
import IconClock from '../../components/icons/IconClock';
import IconAccepted from '../../components/icons/IconAccepted';
import { getTopicBlockType } from './topicBlockTypes';
import { TOPIC_BLOCK_EDITOR_REGISTRY, GenericBlockEditor, renderGenericPreview } from './topicBlockEditors';
import { buildTopicElementPayload } from './coursesPayload';
import { makeDndId } from '../../components/dnd/dndId';
import * as api from './api';

const TOPIC_ELEMENT_BASE = '/api/topicelements';

// Mirrors exercise-templates/BlockCard.jsx - reuses the same
// .exercise-block-* CSS classes (already globally bundled via
// exercise-templates.css) rather than duplicating them in courses.css.
export default function TopicElementCard({ topicId, courseId, block, canMoveUp, canMoveDown, onMove, onDeleted, onSaved }) {
   const [isEditing, setIsEditing] = useState(false);
   const blockType = getTopicBlockType(block.type);
   const registryEntry = TOPIC_BLOCK_EDITOR_REGISTRY[block.type];
   const Editor = registryEntry ? registryEntry.Editor : GenericBlockEditor;
   const renderPreview = registryEntry ? registryEntry.renderPreview : renderGenericPreview;

   // Draggable (into/out of any container) - see TopicElementList.jsx for
   // the DndContext this participates in. Listeners are scoped to the new
   // .exercise-block-drag-handle wrapper only, not the whole toolbar, so
   // the existing move/edit/delete buttons stay completely unaffected.
   //
   // Deliberately NOT applying useSortable's own transform/transition here.
   // Reordering in this feature is handled entirely by the hand-rolled
   // dragPreview overlay (real React DOM-order changes via
   // applyDragPreview), not dnd-kit's own built-in per-item CSS-transform
   // reflow - letting both run at once caused a real bug during
   // development: dnd-kit's own transition animates a sibling's rect while
   // it's mid-flight, and collision detection measuring that same rect
   // mid-animation created a feedback loop (resolve A -> reflow -> sibling
   // rect moves -> stationary pointer now resolves B -> reflow back ->
   // resolves A again -> ...), visible as the drop target flickering
   // between a container and its own child every frame. This card's own
   // droppable/draggable registration (via useSortable) is still needed -
   // only the CSS side-effects are skipped.
   const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id: makeDndId('block', block.id) });

   const handleDelete = () => {
      if(!window.confirm('Чи напевно хочеш видалити цей блок ?')) return;
      // Deletion silently no-oped here before this fix whenever the backend
      // 500s (confirmed real cause: this block has ever been pinned on a
      // canvas, or has a note/marker/approval-request attached - those
      // tables all RESTRICT-delete against topic_element, and nothing here
      // cascaded them) - the block just stayed in the list with zero
      // feedback, indistinguishable from "did nothing happen or is it just
      // slow". Not type-specific to video_embed, applies to every block
      // type; flagged to the backend for a proper cascade-delete fix, this
      // at least stops it from looking broken in the meantime.
      api.deleteOne(TOPIC_ELEMENT_BASE, block.id).then(() => onDeleted()).catch(err => {
         console.error(err);
         window.alert('Не вдалося видалити блок. Можливо, він ще використовується (закріплений на канвасі, має нотатку тощо).');
      });
   };

   const handleSaveChanges = (changes) => {
      const payload = buildTopicElementPayload(topicId, block, changes);
      return api.updateOne(TOPIC_ELEMENT_BASE, block.id, payload)
         .then(() => { setIsEditing(false); onSaved(); })
         .catch(err => console.error(err));
   };

   // Quick toggle, independent of the Editor/edit-mode flow above - a
   // teacher marking "blur this until the student asks to see it" shouldn't
   // need to open the full block editor for it.
   const handleToggleBlur = (e) => {
      e.stopPropagation();
      const payload = buildTopicElementPayload(topicId, block, { blurEnabled: !block.blurEnabled });
      api.updateOne(TOPIC_ELEMENT_BASE, block.id, payload).then(() => onSaved()).catch(err => console.error(err));
   };

   // Only meaningful (and only shown) while blurEnabled - whether the
   // student's "Показати" click needs a teacher's Approve first (default,
   // blurRequiresApproval=true) or unblurs for them immediately with no
   // teacher involved (false) - either way the reveal is still recorded
   // per-student server-side, so a student who already saw it stays
   // unblurred on their next visit regardless of which mode this is.
   const handleToggleApproval = (e) => {
      e.stopPropagation();
      const payload = buildTopicElementPayload(topicId, block, { blurRequiresApproval: !(block.blurRequiresApproval !== false) });
      api.updateOne(TOPIC_ELEMENT_BASE, block.id, payload).then(() => onSaved()).catch(err => console.error(err));
   };

   return (
      <div ref={setNodeRef} className={'exercise-block-card' + (isEditing ? ' exercise-block-card-editing' : '') + (isDragging ? ' exercise-block-card-dragging' : '')}
         style={{ '--accent': blockType ? blockType.accent : '#7f7f7f' }}>
         <div className='exercise-block-card-toolbar'>
            <div className='exercise-block-drag-handle' {...attributes} {...listeners}>
               <IconGrip style={{ width: 12, height: 12 }} />
               <span className='exercise-block-card-type-label'>{blockType ? blockType.label : block.type}</span>
            </div>
            <div className='exercise-block-card-actions'>
               <div className={'exercise-block-move-btn' + (!canMoveUp ? ' disabled' : '')} onClick={() => canMoveUp && onMove(-1)} title='Вгору'>
                  <IconArrow style={{ transform: 'rotate(-90deg)', height: 14, width: 14 }} />
               </div>
               <div className={'exercise-block-move-btn' + (!canMoveDown ? ' disabled' : '')} onClick={() => canMoveDown && onMove(1)} title='Вниз'>
                  <IconArrow style={{ transform: 'rotate(90deg)', height: 14, width: 14 }} />
               </div>
               <div className='exercise-block-edit-btn' onClick={() => setIsEditing(prev => !prev)} title='Редагувати'>
                  <IconEdit style={{ fill: '#7ec2e8', width: 14, height: 14, margin: 0 }} />
               </div>
               <div className={'exercise-block-blur-btn' + (block.blurEnabled ? ' active' : '')} onClick={handleToggleBlur}
                  title={block.blurEnabled ? 'Розмито до підтвердження - клікни щоб вимкнути' : 'Розмити до підтвердження вчителя'}>
                  {block.blurEnabled ? <IconVisibleOff style={{ width: 14, height: 14, margin: 0 }} /> : <IconVisible style={{ width: 14, height: 14, margin: 0 }} />}
               </div>
               {block.blurEnabled ? (
                  <div className={'exercise-block-approval-btn' + (block.blurRequiresApproval === false ? ' auto' : '')} onClick={handleToggleApproval}
                     title={block.blurRequiresApproval === false
                        ? 'Одразу без підтвердження - клікни щоб вимагати підтвердження вчителя'
                        : 'Вимагає підтвердження вчителя - клікни щоб відкривати одразу без підтвердження'}>
                     {block.blurRequiresApproval === false ? <IconAccepted style={{ width: 14, height: 14, margin: 0 }} /> : <IconClock style={{ width: 14, height: 14, margin: 0 }} />}
                  </div>
               ) : ''}
               <div className='exercise-block-delete-btn' onClick={handleDelete} title='Видалити'>
                  <IconDelete style={{ fill: '#e0876c', width: 14, height: 14, margin: 0 }} />
               </div>
            </div>
         </div>
         <div className='exercise-block-card-body'>
            {isEditing ? (
               <Editor block={block} courseId={courseId} onSave={handleSaveChanges} onCancel={() => setIsEditing(false)} />
            ) : renderPreview(block)}
         </div>
      </div>
   );
}
